import { useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslationApp } from "../../lib/i18next";
import Button from "./Button";
import IconButton from "@mui/material/IconButton";
import Loader from "../Loaders/Loader";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "../Form/Checkbox";
import Radio from "@mui/material/Radio";

const ButtonMenu = ({
  showLabel = true,
  showOptionLabels = true,
  loading = false,
  disabled = false,
  options,
  textButton = "",
  colorButton = "primary",
  fullWidth = false,
  iconOnly = false,
  ariaLabel = "",
  sx = {},
  sxMenu = {},
  mode = "button",
  iconButton = null,
  endIcon = null,
  tooltipText,
  edge = "end",
  iconButtonSize,
  loadingProgressSize = 20,
  anchorOrigin = null,
  transformOrigin = null,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [submenuLabel, setSubmenuLabel] = useState(null);

  const open = Boolean(anchorEl);
  const onSelectMenu = (event) => {
    event?.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const onCloseSubmenu = (event) => {
    event?.stopPropagation?.();
    setSubmenuAnchorEl(null);
    setSubmenuLabel(null);
  };
  const onCloseMenu = (event) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
    setSubmenuAnchorEl(null);
    setSubmenuLabel(null);
  };

  const { t } = useTranslationApp();
  const submenuOption = options.find((option) => option.label === submenuLabel);
  const isSubmenuOpen = Boolean(submenuAnchorEl) && Boolean(submenuOption);

  return (
    <>
      {mode === "icon" && (
        <>
          {loading ? (
            <IconButton
              disabled
              sx={sx}
              edge={edge}
              size={iconButtonSize}
              aria-busy="true"
              aria-label={ariaLabel || undefined}
            >
              <CircularProgress
                size={loadingProgressSize}
                sx={{ color: "inherit" }}
              />
            </IconButton>
          ) : tooltipText ? (
            <Tooltip title={tooltipText}>
              <IconButton
                onClick={onSelectMenu}
                sx={sx}
                edge={edge}
                size={iconButtonSize}
                disabled={disabled}
                aria-label={ariaLabel || undefined}
              >
                {iconButton}
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              onClick={onSelectMenu}
              sx={sx}
              edge={edge}
              size={iconButtonSize}
              disabled={disabled}
              aria-label={ariaLabel || undefined}
            >
              {iconButton}
            </IconButton>
          )}
        </>
      )}
      {mode === "button" ? (
        <Button
          loading={loading}
          disabled={disabled}
          fullWidth={fullWidth}
          color={colorButton}
          onClick={onSelectMenu}
          iconOnly={iconOnly}
          startIcon={iconOnly ? iconButton : undefined}
          ariaLabel={ariaLabel || t(textButton)}
          tooltip={
            tooltipText
              ? t(tooltipText)
              : iconOnly && textButton
                ? t(textButton)
                : ""
          }
          sx={sx}
        >
          {!iconOnly && iconButton}
          {showLabel && t(textButton)}
          {!iconOnly && endIcon}
        </Button>
      ) : null}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onCloseMenu}
        {...(anchorOrigin && { anchorOrigin })}
        {...(transformOrigin && { transformOrigin })}
        sx={{ ...sxMenu }}
        MenuListProps={{
          onClick: (e) => e?.stopPropagation?.(),
          onMouseDown: (e) => e?.stopPropagation?.(),
        }}
        PaperProps={{
          style: {
            maxHeight: 48 * 5 + 8,
            overflow: "auto",
          },
        }}
      >
        {options.map((option, index) => {
          if (option.customComponent)
            return <Box key={index}>{option.customComponent}</Box>;
          if (option.submenu) {
            return (
              <MenuItem
                key={option.label}
                disabled={Boolean(option.disabled)}
                onClick={(e) => {
                  e?.stopPropagation();
                  if (option.disabled) return;
                  setSubmenuAnchorEl(e.currentTarget);
                  setSubmenuLabel(option.label);
                }}
              >
                {option.icon ? option.icon : null}{" "}
                {showOptionLabels && t(option.label, option.extrapolation)}
                <ChevronRightIcon fontSize="small" sx={{ ml: "auto" }} />
              </MenuItem>
            );
          }
          const onSelectOption = (event) => {
            event?.stopPropagation();
            if (option.disabled) return;
            option.onClick();
            setAnchorEl(null);
            setSubmenuAnchorEl(null);
            setSubmenuLabel(null);
          };

          const optionItem = (
            <MenuItem
              key={option.key || option.label}
              disabled={Boolean(option.disabled)}
              onClick={onSelectOption}
            >
              {option.icon ? option.icon : null}{" "}
              {showOptionLabels && t(option.label, option.extrapolation)}
            </MenuItem>
          );

          if (!option.tooltip) return optionItem;

          return (
            <Tooltip key={option.key || option.label} title={t(option.tooltip)}>
              <Box component="span" sx={{ display: "block" }}>
                {optionItem}
              </Box>
            </Tooltip>
          );
        })}
      </Menu>
      <Menu
        anchorEl={submenuAnchorEl}
        open={isSubmenuOpen}
        onClose={onCloseSubmenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{
          onClick: (e) => e?.stopPropagation?.(),
          onMouseDown: (e) => e?.stopPropagation?.(),
        }}
        PaperProps={{
          sx: { minWidth: 240 },
        }}
      >
        {(submenuOption?.submenu || []).map((item) => {
          if (item.type === "checkbox") {
            const isItemLoading = Boolean(item.loading);
            return (
              <MenuItem
                key={item.key || item.label}
                disabled={Boolean(item.disabled) || isItemLoading}
                role="menuitemcheckbox"
                aria-checked={Boolean(item.checked)}
                onClick={(e) => {
                  e?.stopPropagation();
                  if (item.disabled || isItemLoading) return;
                  item.onChange(!item.checked);
                }}
              >
                {isItemLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      mr: 1,
                    }}
                  >
                    <Loader size={18} hasMarginTop={false} />
                  </Box>
                ) : (
                  <Checkbox
                    checked={Boolean(item.checked)}
                    disabled={Boolean(item.disabled)}
                    tabIndex={-1}
                    sx={{ p: 0.5, mr: 1 }}
                  />
                )}
                {showOptionLabels && t(item.label, item.extrapolation)}
              </MenuItem>
            );
          }

          if (item.type === "radio") {
            const isItemLoading = Boolean(item.loading);
            const isChecked = Boolean(item.checked);
            return (
              <MenuItem
                key={item.key || item.label}
                disabled={Boolean(item.disabled) || isItemLoading}
                role="menuitemradio"
                aria-checked={isChecked}
                onClick={(e) => {
                  e?.stopPropagation();
                  if (item.disabled || isItemLoading || isChecked) return;
                  item.onChange(item.value);
                }}
              >
                {isItemLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      mr: 1,
                    }}
                  >
                    <Loader size={18} hasMarginTop={false} />
                  </Box>
                ) : (
                  <Radio
                    checked={isChecked}
                    disabled={Boolean(item.disabled)}
                    tabIndex={-1}
                    size="small"
                    sx={{ p: 0.5, mr: 1 }}
                  />
                )}
                {showLabel && t(item.label, item.extrapolation)}
              </MenuItem>
            );
          }

          return (
            <MenuItem
              key={item.key || item.label}
              disabled={Boolean(item.disabled)}
              onClick={(e) => {
                e?.stopPropagation();
                if (item.disabled) return;
                item.onClick?.();
              }}
            >
              {showOptionLabels && t(item.label, item.extrapolation)}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default ButtonMenu;
